import categoryMap from './RiskCategories/category_map';
import {
  getOverview,
  getResultsFindingsSummary,
  getResultsPayloadForExport,
  listDiscoverJobsPage,
} from '../../DataDiscovery/discoverApi';
import { getLandscapeHomeSummary } from '../homeSummaryApi';

const CATEGORY_ORDER = ['personal', 'financial', 'credentials', 'healthcare', 'PHI'];

const normalizeRiskLevel = (level = '') => {
  const upper = String(level).toUpperCase();
  if (upper === 'VERY_HIGH') return 'HIGH';
  if (upper === 'PHI') return 'CRITICAL';
  return upper || 'UNKNOWN';
};

const getProfileSource = async (id) => {
  const numericId = Number(id);
  if (!Number.isFinite(numericId)) {
    throw new Error(`Invalid data source id "${id}".`);
  }
  const summary = await getLandscapeHomeSummary();
  const sources = Array.isArray(summary?.sources) ? summary.sources : [];
  const source = sources.find((s) => s && s.id === numericId);
  if (!source) {
    throw new Error(`No risk profile found for data source #${numericId}.`);
  }
  return source;
};

/**
 * Latest SUCCEEDED discover job id for a source, or null. The risk-overview's
 * `latestDiscoveryId` is the newest run regardless of status — after a cancelled or
 * failed run it points at partial results, and a risk summary built from those reads
 * as "20 of 33,278 tables sensitive" nonsense. The summary should always reflect the
 * last run that actually completed.
 */
const getLatestSuccessfulDiscoveryId = async (dataSourceId) => {
  try {
    const { discoverJobs } = await listDiscoverJobsPage(0, 50, 'startedAt,desc', Number(dataSourceId));
    const succeeded = (discoverJobs || []).find((j) => j && j.status === 'SUCCEEDED' && j.id != null);
    return succeeded ? String(succeeded.id) : null;
  } catch (e) {
    return null;
  }
};

const getLatestDiscoverResults = async (jobId) => {
  if (jobId == null) return { emptyTablesSkipped: 0, payload: {} };
  // Table-index (PeopleSoft-80K) runs: NEVER reassemble the monolith here — that is
  // hundreds of sequential page requests and hangs this page for minutes. The sparse
  // findings-summary carries exactly the sensitive tables/fields getFindingsSummary
  // needs; whole-run totals (which the sparse view can't provide) ride along from the
  // index so the caller can override the summary's table/field counts.
  const overview = await getOverview(jobId).catch(() => null);
  // Tables the worker never sampled because they held zero rows. They are absent from every
  // results shape, so the KPI strip reports its denominator as "tables PROCESSED" and names the
  // skipped ones separately — otherwise a run over an empty schema reads as "0 of 0 tables".
  const emptyTablesSkipped = Number(overview?.empty_tables_skipped_count) || 0;
  if (overview && overview.tableIndex) {
    const fs = await getResultsFindingsSummary(jobId);
    return {
      emptyTablesSkipped,
      payload: { tables: Array.isArray(fs?.tables) ? fs.tables : [] },
      indexTotals: {
        totalTables: Number(overview.tableIndex.total_tables) || 0,
        totalFields: Number(overview.tableIndex.total_fields) || 0,
      },
    };
  }
  // Precomputed whole-run summary (worker, 2026-10-01). When present this is the WHOLE
  // answer for this page: the alternative below reassembles the per-file tables from every
  // folder chunk — 361 requests at ~1,278 ms each (~7.7 min) on the motivating run, fired
  // concurrently via Promise.all so most of them fail and are silently dropped by
  // `.catch(() => null)`, leaving the page to UNDERCOUNT without erroring. The summary is
  // ~4 KB and one read.
  const summary = overview?.runSummary;
  if (summary) {
    return { emptyTablesSkipped, payload: {}, runSummary: summary };
  }
  // Folder Search runs persist only the folder INDEX (payload.folders, no tables) —
  // getResultsPayloadForExport reassembles the per-file tables from the folder chunks,
  // so the risk summary reflects the actual files instead of an empty table list.
  // Pre-summary runs only; classic runs pass through unchanged.
  const payload = await getResultsPayloadForExport(jobId);
  return { emptyTablesSkipped, payload: payload || {} };
};

/**
 * Map a worker run_summary onto the shape getFindingsSummary returns, so the page renders
 * identically whether the numbers were precomputed or reassembled.
 *
 * Items vs fields: file runs count FILES (the vocabulary those pages already use) and report
 * no field totals at all, because document "fields" are synthesised per page at assembly time
 * and were never persisted — the worker omits them rather than sending a zero that would read
 * as "no fields found".
 */
export const findingsSummaryFromRunSummary = (summary) => {
  const entities = summary?.entities || {};
  const totalsByEntity = {};
  Object.entries(entities).forEach(([entity, stat]) => {
    totalsByEntity[entity] = Number(stat?.instances) || 0;
  });
  const findingsValue = Object.entries(totalsByEntity)
    .sort((a, b) => b[1] - a[1])
    .map(([entity]) => ({ entity }));

  const totalItems = Number(summary?.total_items) || 0;
  const itemsWithFindings = Number(summary?.items_with_findings) || 0;
  // Denominator for the category scores: fields when the run has them, else items.
  const denominator = Math.max(Number(summary?.total_fields) || totalItems, 1);

  const categoryScores = {};
  CATEGORY_ORDER.forEach((category) => {
    const members = categoryMap?.[category] || [];
    // Reach is the MAX over the category's entities, not the sum: the per-entity item counts
    // overlap (one file holding both SSN and ACCOUNT_NUMBER is one file), and summing them
    // would overstate a category purely because it has more member entities.
    let reach = 0;
    members.forEach((entity) => {
      const items = Number(entities[entity]?.items) || 0;
      if (items > reach) reach = items;
    });
    const topFindings = Object.entries(totalsByEntity)
      .filter(([entity]) => members.includes(entity))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([entity]) => entity);
    categoryScores[category] = {
      score: Math.min(100, Math.ceil((reach / denominator) * 100)),
      topFindings,
    };
  });

  return {
    findingsValue,
    categoryScores,
    totalTablesValue: totalItems,
    totalFieldsValue: Number(summary?.total_fields) || totalItems,
    sensitiveTablesValue: itemsWithFindings,
    sensitiveFieldsValue: Number(summary?.fields_with_findings) || itemsWithFindings,
  };
};

const getFindingsSummary = (tables = []) => {
  const totalsByEntity = {};
  let totalTablesValue = 0;
  let totalFieldsValue = 0;
  let sensitiveTablesValue = 0;
  let sensitiveFieldsValue = 0;
  const categoryFieldSets = {};

  CATEGORY_ORDER.forEach((category) => {
    categoryFieldSets[category] = new Set();
  });

  tables.forEach((table) => {
    totalTablesValue += 1;
    let tableHasSensitiveField = false;
    const fields = Array.isArray(table?.fields) ? table.fields : [];
    totalFieldsValue += fields.length;

    fields.forEach((field) => {
      const fieldKey = `${table?.name || ''}::${field?.name || ''}`;
      const findings = field?.findings || {};
      const entries = Object.entries(findings);
      if (entries.length > 0) {
        tableHasSensitiveField = true;
        sensitiveFieldsValue += 1;
      }

      entries.forEach(([entity, count]) => {
        totalsByEntity[entity] = (totalsByEntity[entity] || 0) + Number(count || 0);
        CATEGORY_ORDER.forEach((category) => {
          const entities = categoryMap?.[category] || [];
          if (entities.includes(entity)) {
            categoryFieldSets[category].add(fieldKey);
          }
        });
      });
    });

    if (tableHasSensitiveField) sensitiveTablesValue += 1;
  });

  const findingsValue = Object.entries(totalsByEntity)
    .sort((a, b) => b[1] - a[1])
    .map(([entity]) => ({ entity }));

  const categoryScores = {};
  const denominator = Math.max(totalFieldsValue, 1);
  CATEGORY_ORDER.forEach((category) => {
    const fieldCount = categoryFieldSets[category].size;
    const topFindings = Object.entries(totalsByEntity)
      .filter(([entity]) => (categoryMap?.[category] || []).includes(entity))
      .sort((a, b) => b[1] - a[1])
      .slice(0, 3)
      .map(([entity]) => entity);
    categoryScores[category] = {
      score: Math.min(100, Math.ceil((fieldCount / denominator) * 100)),
      topFindings,
    };
  });

  return {
    findingsValue,
    categoryScores,
    totalTablesValue,
    totalFieldsValue,
    sensitiveTablesValue,
    sensitiveFieldsValue,
  };
};

export {
  getProfileSource,
  getLatestDiscoverResults,
  getLatestSuccessfulDiscoveryId,
  getFindingsSummary,
  normalizeRiskLevel,
};